/**
 * Centralized tooltip and help text for Memory page components.
 * Keeps all user-facing explainer text in one place for consistency.
 */

// Column header tooltips for the Deep Dive table
export const COLUMN_TOOLTIPS: Record<string, string> = {
  memory_type: 'The category of memory being tracked. Working memory is short-term context, conversation memory is the chat history, and tool cache stores tool call results.',
  operation: 'What happened to the memory. Write = new data added, Read = data retrieved, Update = data modified, Prune = old data removed to free space.',
  token_count: 'How many tokens are currently stored in this memory snapshot. Higher values mean more context is being used.',
  max_tokens: 'The maximum number of tokens this memory type can hold before pruning is needed.',
  utilization: 'How full the context window is. Above 70% is a warning, above 90% is critical and may cause data loss from pruning.',
  run_id: 'The unique identifier for the agent run that generated this memory snapshot.',
  timestamp: 'When this memory snapshot was captured.',
};

export interface MetricExplanation {
  title: string;
  healthy: string;
  warning: string;
  critical: string;
}

// Metric card explainers for Health Summary
export const METRIC_EXPLANATIONS: Record<string, MetricExplanation> = {
  context_usage: {
    title: 'Context Usage',
    healthy: 'Your agents are using memory efficiently with plenty of room to grow.',
    warning: 'Some agents are approaching their context limits. Consider reviewing their memory management.',
    critical: 'Agents are hitting context limits. This may cause important information to be pruned.',
  },
  cache_efficiency: {
    title: 'Cache Efficiency',
    healthy: 'Cache is working well, reducing redundant operations.',
    warning: 'Cache hit rate is below optimal. Agents may be making unnecessary calls.',
    critical: 'Very low cache utilization. Consider improving caching strategies.',
  },
  pruning_activity: {
    title: 'Pruning Activity',
    healthy: 'Minimal pruning occurring, which means memory is being managed well.',
    warning: 'Frequent pruning detected. Some agents may be exceeding their memory budgets.',
    critical: 'Heavy pruning is happening. Agents are likely losing important context.',
  },
  runs_at_risk: {
    title: 'Runs at Risk',
    healthy: 'No agent runs are in danger of hitting context limits.',
    warning: 'Some runs are approaching context limits and may need attention.',
    critical: 'Multiple runs are at or near context limits. Review them in Run Stories.',
  },
};

export interface HealthBanner {
  message: string;
  color: string;
  textColor: string;
}

// Health banner messages
export const HEALTH_BANNERS: Record<string, HealthBanner> = {
  critical: {
    message: 'Your agents need attention — some runs are hitting context limits',
    color: 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800',
    textColor: 'text-red-800 dark:text-red-200',
  },
  warning: {
    message: 'Mostly healthy, but some runs are approaching context limits',
    color: 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800',
    textColor: 'text-yellow-800 dark:text-yellow-200',
  },
  healthy: {
    message: "Your agents' memory is healthy — all systems running smoothly",
    color: 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800',
    textColor: 'text-green-800 dark:text-green-200',
  },
};

// Recommendations based on thresholds
export const RECOMMENDATIONS: Record<string, string> = {
  high_utilization: 'Consider increasing context window limits for agents that frequently exceed 80% usage.',
  many_prunes: 'Reduce pruning frequency by optimizing what data agents store in memory, or increase context windows.',
  low_cache: 'Improve cache utilization by ensuring agents reuse tool results and avoid redundant calls.',
  runs_at_risk: 'Review the affected runs in Run Stories to understand which agents need memory optimization.',
};

// "What is Agent Memory?" educational content
export const WHAT_IS_AGENT_MEMORY = {
  title: 'What is Agent Memory?',
  paragraphs: [
    'Agent Memory tracks how your AI agents manage their context window — the limited amount of information they can "remember" at any given time.',
    "Every time an agent reads, writes, or prunes memory, a snapshot is captured here. This helps you understand if your agents are using their context efficiently or if they're running into limits that could affect response quality.",
    'Key concepts: Context window is the total space available. Utilization is how much of that space is being used. Pruning happens when the agent needs to forget old information to make room for new data.',
  ],
};
